import * as React from 'react';

export interface MembershipOption {
  price_id: string;
  label: string;
  tier: 'clinical' | 'non_clinical';
  term: string;
  months: number;
  period_total: number;
  monthly_equivalent: number;
  base_price: number;
  per_location: number;
  current: boolean;
}

export interface ModifyMembershipFormProps {
  form_path: string;
  form_authenticity_token: string;
  subscription: string;
  return_url?: string | null;
  options: MembershipOption[];
  selected_price_id: string;
  quantity: number;
  old_quantity: number;
  max_locations: number;
  phone: string;
}

const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const periodWord = (months: number) => (months === 12 ? 'year' : months === 3 ? 'quarter' : 'month');
const TIER_LABEL: Record<string, string> = { clinical: 'Clinical', non_clinical: 'Non-Clinical' };

// Priced client-side from base_price + per_location so the tiles and the
// running total react to the stepper without a round trip. The server prices
// the same way (MembershipOptions.period_total) and the review card the POST
// returns is the number that actually gets charged.
export function periodTotal(option: MembershipOption, quantity: number): number {
  const extra = Math.max(quantity, 1) - 1;
  return Math.round((option.base_price + extra * option.per_location) * 100) / 100;
}

export default function ModifyMembershipForm({
  form_path, form_authenticity_token, subscription, return_url, options,
  selected_price_id, quantity: initialQuantity, old_quantity, max_locations, phone,
}: ModifyMembershipFormProps) {
  const [quantity, setQuantity] = React.useState(initialQuantity);
  const [priceId, setPriceId] = React.useState(selected_price_id);
  const currentId = options.find((o) => o.current)?.price_id ?? selected_price_id;
  const selected = options.find((o) => o.price_id === priceId) ?? options[0];
  const total = periodTotal(selected, quantity);
  const unchanged = quantity === old_quantity && priceId === currentId;
  const atCap = quantity >= max_locations;
  const tiers = ['non_clinical', 'clinical'].filter((t) => options.some((o) => o.tier === t));

  return (
    <form action={form_path} method="post" className="mm-form">
      <input type="hidden" name="authenticity_token" value={form_authenticity_token} />
      <input type="hidden" name="subscription" value={subscription} />
      <input type="hidden" name="return_url" value={return_url ?? ''} />
      <input type="hidden" name="stripe_plan" value={priceId} />
      <input type="hidden" name="new_quantity" value={quantity} />

      <fieldset className="mm-section">
        <legend className="mm-section__title">Locations</legend>
        <div className="mm-stepper">
          <button
            type="button"
            className="mm-stepper__btn"
            aria-label="Remove a location"
            disabled={quantity <= 1}
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          >
            &minus;
          </button>
          <input id="mm-quantity" aria-label="Locations" className="mm-stepper__value" value={String(quantity)} readOnly />
          <button
            type="button"
            className="mm-stepper__btn"
            aria-label="Add a location"
            disabled={atCap}
            onClick={() => setQuantity((q) => Math.min(max_locations, q + 1))}
          >
            +
          </button>
        </div>
        <p className="mm-hint">Each additional location adds {money(selected.per_location)} per {periodWord(selected.months)}.</p>
        {atCap && <p className="mm-hint mm-hint--strong">For more than {max_locations} locations call {phone}.</p>}
      </fieldset>

      <fieldset className="mm-section">
        <legend className="mm-section__title">Plan and billing term</legend>
        {tiers.map((tier) => (
          <div key={tier} className="mm-tier">
            <h4 className="mm-tier__title">{TIER_LABEL[tier]}</h4>
            <div className="mm-tiles">
              {options.filter((o) => o.tier === tier).map((o) => {
                const tileTotal = periodTotal(o, quantity);
                const monthly = tileTotal / o.months;
                const baseline = options.find((b) => b.tier === o.tier && b.months === 1);
                const savings = baseline && o.months > 1 ? Math.round((1 - monthly / periodTotal(baseline, quantity)) * 100) : 0;
                const id = `mm-plan-${o.price_id}`;
                return (
                  <label key={o.price_id} htmlFor={id} className={`mm-tile${o.price_id === priceId ? ' mm-tile--selected' : ''}`}>
                    <input
                      id={id}
                      type="radio"
                      name="mm_plan"
                      value={o.price_id}
                      checked={o.price_id === priceId}
                      onChange={() => setPriceId(o.price_id)}
                    />
                    <span className="mm-tile__label">
                      {o.label}
                      {o.current && <span className="mm-tile__badge">Current</span>}
                    </span>
                    <span className="mm-tile__price">{money(tileTotal)} / {periodWord(o.months)}</span>
                    {o.months > 1 && (
                      <span className="mm-tile__sub">
                        {money(monthly)} per month{savings > 0 ? `, save ${savings}%` : ''}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </fieldset>

      <div className="mm-total">
        <span>New total: {money(total)} every {periodWord(selected.months)}</span>
        {selected.months > 1 && <small>about {money(total / selected.months)} per month</small>}
      </div>

      <button type="submit" className="btn-modern btn-modern--primary" disabled={unchanged}>Review changes</button>
    </form>
  );
}
